// CameraView.jsx - Updated with role-based permissions for prestige/prestige_user

import React, { useState, useEffect, useContext } from "react";
import { Modal, Table, Pagination, Select, Input, Button, Tag, message } from "antd";
import { Camera, X, Shield, Eye, Search, XCircle, Edit2, Save, RotateCw } from "lucide-react";
import apiInstance from "../../utils/axiosDash";
import HlsStreamPlayer from "./hlsstreamer";
import { RootContext } from "../../context/RootContext"; // Import your auth context
import WebRTCStreamPlayer from "./WebRTCStreamPlayer";

const { Option } = Select;

// ─── Helper: Extract subnet ID from IP ────────────────────────────────
const getSubnetIdFromIp = (ip) => {
    if (!ip) return null;
    const parts = ip.split('.');
    if (parts.length === 4) {
        return parts.slice(0, 3).join('_');
    }
    return null;
};

// ─── Helper: Extract numeric ID from cameraId ────────────────────────────
const extractNumericId = (cameraId) => {
    if (!cameraId) return null;
    const match = cameraId.match(/\d+_(\d+)/);
    if (match) {
        return match[1];
    }
    const numMatch = cameraId.match(/\d+/);
    return numMatch ? numMatch[0] : cameraId;
};

// ─── Helper: Check if user has edit permissions ──────────────────────────
const hasEditPermission = (username) => {
    // prestige_user should NOT see the edit button
    // prestige CAN see the edit button
    const restrictedUsers = ['prestige_user'];
    return !restrictedUsers.includes(username?.toLowerCase());
};

// ─── Modal Stream Content ────────────────────────────────────────────
const StreamModal = ({ cam, onClose }) => {
    const [streamKey, setStreamKey] = useState(0);

    useEffect(() => {
        setStreamKey((k) => k + 1);
    }, [cam?.cameraId]);

    if (!cam) return null;

    const subnetId = getSubnetIdFromIp(cam.ip);
    const numericId = extractNumericId(cam.cameraId);

    return (
        <div className="flex flex-col gap-0">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 rounded bg-green-50 border border-green-200 px-2 py-1">
                        <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                        </span>
                        <span className="text-[9px] font-bold tracking-widest text-green-600 uppercase">Live</span>
                    </div>
                    <div>
                        <p className="text-sm font-bold text-gray-900 leading-none">{cam.name}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{cam.location} · IP: {cam.ip}</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="flex items-center justify-center h-8 w-8 rounded-lg bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>

            <div className="bg-black">
                <HlsStreamPlayer
                    key={streamKey}
                    camId={numericId}
                    camIp={cam.ip}
                    subnetId={subnetId}
                    fullCamId={cam.cameraId}
                    autoStart={true}
                    width="100%"
                    height="480px"
                    onStatusChange={(status, data) => {}}
                    onError={(err) => {
                        console.error('🔍 [NEW] Stream Error:', {
                            error: err,
                            camId: numericId,
                            camIp: cam.ip
                        });
                    }}
                />
                {/* <WebRTCStreamPlayer
    key={streamKey}
    camId={numericId}
    camIp={cam.ip}
    subnetId={subnetId}
    fullCamId={cam.cameraId}
    autoStart={true}
    width="100%"
    height="480px"
/> */}
{/* <WebRTCStreamPlayer
    key={streamKey}
    camId={numericId}
    camIp={cam.ip}
    subnetId={subnetId}
    fullCamId={cam.cameraId}
    autoStart={true}
    width="100%"
    height="480px"
/> */}
            </div>

            <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 border-t border-gray-200">
                <p className="text-[10px] text-gray-600 font-mono tracking-wide">
                    {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    {" · "}
                    <span id="modal-clock">{new Date().toLocaleTimeString()}</span>
                </p>
                <div className="flex items-center gap-1.5 text-gray-600">
                    <Shield size={11} />
                    <span className="text-[10px] tracking-wide">Encrypted stream</span>
                </div>
            </div>
        </div>
    );
};

// ─── Main Camera View ──────────────────────────────────────────────
const CameraView = () => {
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalCam, setModalCam] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", location: "" });
    
    const [currentPage, setCurrentPage] = useState(() => {
    const navigationType = performance.getEntriesByType("navigation")[0]?.type;

    if (navigationType === "reload") {
            const savedPage = sessionStorage.getItem("prestige_camera_current_page");
            return savedPage ? Number(savedPage) : 1;
        }

        return 1;
    });

    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [locationFilter, setLocationFilter] = useState("all");
    const [totalCameras, setTotalCameras] = useState(0);
    const [locations, setLocations] = useState([]);
    const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });
    
    // ─── Get current user from context ─────────────────────────────────────
    // Option 1: Using RootContext (recommended since you're using it in login)
    const { state } = useContext(RootContext);
    const currentUsername = state?.user || getUsernameFromLocalStorage();

    useEffect(() => {
        sessionStorage.setItem(
            "prestige_camera_current_page",
            String(currentPage)
        );
    }, [currentPage]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem("prestige_camera_current_page");
        };
    }, []);
    
    // Option 2: Fallback to localStorage if context is not available
    function getUsernameFromLocalStorage() {
        try {
            const citriot = JSON.parse(localStorage.getItem('citriot'));
            return citriot?.auth?.user || 'admin';
        } catch (error) {
            console.error('Error getting user from localStorage:', error);
            return 'admin';
        }
    }
    
    // Determine if user can edit
    const canEdit = hasEditPermission(currentUsername);

    // ─── API CALLS ──────────────────────────────────────────────────────────

    const fetchCameras = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage,
                limit: pageSize,
                search: searchTerm,
                location: locationFilter,
            });
            
            const response = await apiInstance.get(`/prestige-fetch-cameras?${params}`);
            
            if (response.data.success) {
                setCameras(response.data.cameras);
                setTotalCameras(response.data.total);
                if (response.data.locations) {
                    setLocations(response.data.locations);
                }
                
                if (response.data.cameras) {
                    const online = response.data.cameras.filter(c => c.status === "online").length;
                    const offline = response.data.cameras.filter(c => c.status === "offline").length;
                    setStats({
                        total: response.data.total,
                        online,
                        offline
                    });
                }
            }
        } catch (error) {
            console.error("❌ Error fetching cameras:", error);
            message.error("Failed to load cameras");
        } finally {
            setLoading(false);
        }
    };

    const updateCamera = async (cameraId, data) => {
        try {
            const response = await apiInstance.put(
                `/prestige-update-camera/${cameraId}`,
                data
            );
            return response.data;
        } catch (error) {
            console.error("Error updating camera:", error);
            throw error;
        }
    };

    // ─── EFFECTS ────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchCameras();
    }, [currentPage, pageSize, searchTerm, locationFilter]);

    // ─── HANDLERS ───────────────────────────────────────────────────────────

    const startEditing = (camera) => {
        setEditingId(camera.cameraId);
        setEditForm({ name: camera.name, location: camera.location });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditForm({ name: "", location: "" });
    };

    const saveEdit = async (cameraId) => {
        try {
            const response = await updateCamera(cameraId, editForm);
            if (response.success) {
                message.success("Camera updated successfully");
                setEditingId(null);
                await fetchCameras();
            }
        } catch (error) {
            message.error("Failed to update camera");
            console.error(error);
        }
    };

    const handleKeyPress = (e, cameraId) => {
        if (e.key === "Enter") {
            saveEdit(cameraId);
        } else if (e.key === "Escape") {
            cancelEditing();
        }
    };

    const openCamera = (cam) => {
        if (!cam.cameraId) {
            console.error("❌ Missing cameraId:", cam);
            message.error("Camera ID is missing");
            return;
        }
        
        if (!cam.ip) {
            console.error("❌ Missing IP address:", cam);
            message.error("Camera IP is missing");
            return;
        }
        
        if (cam.status !== "online") {
            console.warn("⚠️ Camera is offline, cannot view:", cam);
            message.warning("Camera is offline");
            return;
        }
        
        setModalCam(cam);
    };
    
    const closeCamera = () => setModalCam(null);

    const clearAllFilters = () => {
        setSearchTerm("");
        setLocationFilter("all");
        setCurrentPage(1);
    };

    // ─── TABLE COLUMNS ─────────────────────────────────────────────────────

    const columns = [
        {
            title: "ID",
            dataIndex: "cameraId",
            key: "cameraId",
            width: 80,
            render: (cameraId) => <span className="font-mono text-xs text-gray-500">{cameraId}</span>,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (name, record) => {
                if (editingId === record.cameraId) {
                    return (
                        <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            onKeyDown={(e) => handleKeyPress(e, record.cameraId)}
                            className="w-full"
                            autoFocus
                            size="small"
                        />
                    );
                }
                return <span className="font-medium text-gray-900">{name}</span>;
            },
        },
        {
            title: "IP Address",
            dataIndex: "ip",
            key: "ip",
            render: (ip) => <span className="font-mono text-xs text-gray-600">{ip}</span>,
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            render: (location, record) => {
                if (editingId === record.cameraId) {
                    return (
                        <Input
                            value={editForm.location}
                            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                            onKeyDown={(e) => handleKeyPress(e, record.cameraId)}
                            className="w-full"
                            size="small"
                        />
                    );
                }
                return (
                    <Tag color="blue" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                        {location}
                    </Tag>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <div className="flex items-center gap-2">
                    {status === "online" ? (
                        <>
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                            </span>
                            <span className="text-green-600 text-xs font-medium">Online</span>
                        </>
                    ) : (
                        <>
                            <span className="h-2 w-2 rounded-full bg-gray-400" />
                            <span className="text-gray-500 text-xs font-medium">Offline</span>
                        </>
                    )}
                </div>
            ),
        },
        {
            title: "Action",
            key: "action",
            width: canEdit ? 200 : 120, // Adjust width based on permissions
            render: (_, record) => {
                if (editingId === record.cameraId) {
                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                type="primary"
                                size="small"
                                icon={<Save size={14} />}
                                onClick={() => saveEdit(record.cameraId)}
                                className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
                            >
                                Save
                            </Button>
                            <Button
                                size="small"
                                onClick={cancelEditing}
                                className="flex items-center gap-1"
                            >
                                Cancel
                            </Button>
                        </div>
                    );
                }
                return (
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            size="small"
                            icon={<Eye size={14} />}
                            onClick={() => openCamera(record)}
                            disabled={record.status !== "online"}
                            className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                        >
                            View
                        </Button>
                        {/* Only show Edit button if user has permission */}
                        {canEdit && (
                            <Button
                                size="small"
                                icon={<Edit2 size={14} />}
                                onClick={() => startEditing(record)}
                                className="flex items-center gap-1 border-gray-300 hover:border-blue-500"
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    // ─── RENDER ─────────────────────────────────────────────────────────────

    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes ping {
                    75%, 100% { transform: scale(2); opacity: 0; }
                }
                .ant-modal .ant-modal-content {
                    padding: 0 !important;
                    background: #ffffff !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 14px !important;
                    overflow: hidden;
                }
                .ant-modal-mask { 
                    background: rgba(0,0,0,0.65) !important; 
                    backdrop-filter: blur(4px); 
                }
                .ant-table {
                    background: #ffffff !important;
                }
                .ant-table-thead > tr > th {
                    background: #f9fafb !important;
                    color: #374151 !important;
                    border-bottom: 2px solid #e5e7eb !important;
                    font-weight: 600 !important;
                }
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f3f4f6 !important;
                    background: #ffffff !important;
                }
                .ant-table-tbody > tr:hover > td {
                    background: #f9fafb !important;
                }
                .ant-table-tbody > tr > td {
                    color: #111827 !important;
                }
                .ant-pagination .ant-pagination-item {
                    background: #ffffff !important;
                    border-color: #d1d5db !important;
                }
                .ant-pagination .ant-pagination-item a {
                    color: #374151 !important;
                }
                .ant-pagination .ant-pagination-item-active {
                    background: #3b82f6 !important;
                    border-color: #3b82f6 !important;
                }
                .ant-pagination .ant-pagination-item-active a {
                    color: #ffffff !important;
                }
                .ant-pagination .ant-pagination-prev button,
                .ant-pagination .ant-pagination-next button {
                    color: #374151 !important;
                }
                .ant-pagination .ant-pagination-prev button:hover,
                .ant-pagination .ant-pagination-next button:hover {
                    color: #3b82f6 !important;
                }
                .ant-select-selector {
                    background: #ffffff !important;
                    border-color: #d1d5db !important;
                    color: #111827 !important;
                }
                .ant-select-dropdown {
                    background: #ffffff !important;
                }
                .ant-select-item {
                    color: #111827 !important;
                }
                .ant-select-item:hover {
                    background: #f3f4f6 !important;
                }
                .ant-input {
                    background: #ffffff !important;
                    border-color: #d1d5db !important;
                    color: #111827 !important;
                }
                .ant-input::placeholder {
                    color: #9ca3af !important;
                }
                .ant-input-prefix {
                    color: #9ca3af !important;
                }
                .ant-select-arrow {
                    color: #9ca3af !important;
                }
                .ant-tag-blue {
                    background: #eff6ff !important;
                    border-color: #bfdbfe !important;
                    color: #1e40af !important;
                }
                .ant-btn-primary {
                    background: #3b82f6 !important;
                    border-color: #3b82f6 !important;
                }
                .ant-btn-primary:hover {
                    background: #2563eb !important;
                    border-color: #2563eb !important;
                }
                .ant-btn-primary[disabled] {
                    background: #d1d5db !important;
                    border-color: #d1d5db !important;
                    color: #9ca3af !important;
                }
                .ant-select-focused .ant-select-selector {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1) !important;
                }
                .ant-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1) !important;
                }
                .clear-filters-btn:hover {
                    background: #f3f4f6 !important;
                    border-color: #d1d5db !important;
                    color: #111827 !important;
                }
            `}</style>

            {/* ── Page Header ── */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-1.5">
                    <Camera size={13} className="text-blue-600" />
                    <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-blue-600">
                        Live Surveillance
                    </p>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight text-gray-900">
                            Camera Management
                        </h1>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <p className="text-sm text-gray-400">
                                <span className="font-semibold text-gray-600">{stats.total}</span> total cameras
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={fetchCameras}
                        icon={<RotateCw size={16} />}
                        className="flex items-center gap-2"
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {/* ── Filters ── */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
                <Input
                    prefix={<Search size={16} className="text-gray-400" />}
                    placeholder="Search by ip or name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64"
                    allowClear
                />
                <Select
                    value={locationFilter}
                    onChange={setLocationFilter}
                    className="w-40"
                    placeholder="Location"
                >
                    <Option value="all">All Locations</Option>
                    {locations.map((loc) => (
                        <Option key={loc} value={loc}>{loc}</Option>
                    ))}
                </Select>
                <Button
                    onClick={clearAllFilters}
                    icon={<XCircle size={14} />}
                    className="flex items-center gap-1.5 border-gray-300 text-gray-700 hover:text-gray-900 clear-filters-btn"
                >
                    Clear Filters
                </Button>
                <span className="text-sm text-gray-500 ml-auto">
                    Showing {totalCameras > 0 ? (currentPage - 1) * pageSize + 1 : 0} -{" "}
                    {Math.min((currentPage - 1) * pageSize + pageSize, totalCameras)} of {totalCameras}
                </span>
            </div>

            {/* ── Table ── */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <Table
                    columns={columns}
                    dataSource={cameras}
                    rowKey="cameraId"
                    pagination={false}
                    className="bg-white"
                    scroll={{ x: 1000 }}
                    loading={loading}
                />
            </div>

            {/* ── Pagination ── */}
            {!loading && totalCameras > 0 && (
                <div className="mt-6 flex justify-between items-center">
                    <Select
                        value={pageSize}
                        onChange={(value) => {
                            setPageSize(value);
                            setCurrentPage(1);
                        }}
                        className="w-24"
                    >
                        <Option value={10}>10</Option>
                        <Option value={20}>20</Option>
                        <Option value={50}>50</Option>
                        <Option value={100}>100</Option>
                    </Select>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalCameras}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </div>
            )}

            {/* ── Modal ── */}
            <Modal
                open={!!modalCam}
                onCancel={closeCamera}
                footer={null}
                width={860}
                closeIcon={null}
                destroyOnClose
                centered
            >
                <StreamModal cam={modalCam} onClose={closeCamera} />
            </Modal>
        </>
    );
};

export default CameraView;